import { Users, TrendingUp, Globe, ArrowRight, Sparkles, CheckCircle2, Rocket, Star, BarChart3, Award } from "lucide-react";

export default function TalentTransformation() {
  const stats = [
    {
      value: "500+",
      label: "Consultants Placed",
      icon: Users,
      color: "#0058a9",
      lightBg: "#e8f2fa"
    },
    {
      value: "95%",
      label: "Client Retention",
      icon: TrendingUp,
      color: "#0d9488",
      lightBg: "#f0fdfa"
    },
    {
      value: "10+",
      label: "Countries Served",
      icon: Globe,
      color: "#2c3e50",
      lightBg: "#f5f6f7"
    }
  ];

  const benefits = [
    {
      title: "Move Faster",
      description: "Accelerated hiring timelines with pre-vetted talent pools",
      icon: Rocket
    },
    {
      title: "Reduce Complexity",
      description: "Streamlined recruitment process from sourcing to onboarding",
      icon: BarChart3
    },
    {
      title: "Better Outcomes",
      description: "Strategic talent alignment for business growth",
      icon: Award
    }
  ];

  return (
    <section
      className="w-full py-20 md:py-28 lg:py-32"
      style={{ backgroundColor: "#ffffff" }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        
        {/* Header Section */}
        <div className="text-center mb-12">
          {/* Bubble tag */}
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-2 h-2 bg-black rounded-full"></div>
            <p 
              className="text-sm font-medium uppercase tracking-wider"
              style={{ 
                fontFamily: '"Open Sans", sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#081120'
              }}
            >
              The Kapil Tech Difference
            </p>
          </div>

          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-5"
            style={{ 
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 600,
              color: '#081120'
            }}
          >
            The Right Talent Can <br />
            <span className="text-transparent bg-clip-text" 
                  style={{ backgroundImage: 'linear-gradient(135deg, #0058a9, #0d9488)' }}>
              Transform Your Business
            </span>
          </h2>
        </div>

        {/* Stats Section - Modern Card Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div
                key={idx}
                className="group relative rounded-2xl p-6 text-center transition-all duration-500 hover:-translate-y-2"
                style={{ 
                  backgroundColor: stat.lightBg,
                }}
              >
                {/* Animated background effect on hover */}
                <div 
                  className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-10"
                  style={{ backgroundColor: stat.color }}
                ></div>
                
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <Icon className="w-7 h-7" style={{ color: stat.color }} />
                </div>
                
                <h3
                  className="text-4xl md:text-5xl font-bold mb-2"
                  style={{ 
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 500,
                    color: stat.color
                  }}
                >
                  {stat.value}
                </h3>
                <p
                  className="text-sm font-medium text-center"
                  style={{ 
                    fontFamily: '"Open Sans", sans-serif',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#555658',
                    letterSpacing: '0.5px'
                  }}
                >
                  {stat.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Main Content - Split Layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left Side - Main Message */}
          <div>
            <div className="mb-6">
            <img
                src="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/starrotation.avif"
                alt="Rotating Star"
                className="w-16 h-16 animate-[spin_15s_linear_infinite] brightness-0"
            />
            </div>
            
            <p
              className="text-lg leading-relaxed mb-6"
              style={{ 
                fontFamily: '"Open Sans", sans-serif',
                fontSize: '18px',
                lineHeight: '170%',
                color: '#081120',
                fontWeight: 500
              }}
            >
              When you partner with Kapil Tech, you gain more than a recruitment provider.
            </p>
            
            <p
              className="text-base leading-relaxed mb-8"
              style={{ 
                fontFamily: '"Open Sans", sans-serif',
                fontSize: '16px',
                lineHeight: '180%',
                color: '#555658'
              }}
            >
              You gain a strategic staffing partner committed to helping your organization attract, 
              hire, and retain exceptional technology talent.
            </p>

            {/* Benefits Chips */}
            <div className="flex flex-wrap gap-3 mb-8">
              {benefits.map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 hover:scale-105 cursor-default"
                    style={{ 
                      backgroundColor: '#f5f5f5',
                    }}
                  >
                    <Icon className="w-4 h-4" style={{ color: '#0058a9' }} />
                    <span
                      className="text-sm font-medium"
                      style={{ 
                        fontFamily: '"Open Sans", sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#081120'
                      }}
                    >
                      {benefit.title}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* CTA Button */}
            <div 
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 cursor-pointer"
              style={{ 
                backgroundColor: '#081120',
                transform: 'translateY(0)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span
                className="text-sm font-semibold"
                style={{ 
                  fontFamily: '"Open Sans", sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff'
                }}
              >
                Let's Transform Your Team
              </span>
              <ArrowRight className="w-4 h-4" style={{ color: '#ffffff' }} />
            </div>
          </div>

          {/* Right Side - Visual/Quote Box */}
          <div 
            className="relative rounded-2xl p-8 overflow-hidden"
            style={{ 
              backgroundColor: '#f8f9fa',
              border: '1px solid #eef2f6'
            }}
          >
            {/* Decorative gradient circle */}
            <div 
              className="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-10"
              style={{ backgroundColor: '#0058a9' }}
            ></div>
            
            <div className="relative z-10">
              <div className="flex gap-1 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 fill-current" style={{ color: '#fbbf24' }} />
                ))}
              </div>
              
              <p
                className="text-xl md:text-2xl font-semibold leading-relaxed mb-6"
                style={{ 
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: '22px',
                  fontWeight: 600,
                  color: '#081120'
                }}
              >
                "Whether you're hiring one specialist or building an entire project team, we help you achieve better business outcomes."
              </p>
              
              <div className="flex items-center gap-3 pt-4 border-t" style={{ borderColor: '#e5e7eb' }}>
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#e8f2fa' }}
                >
                  <CheckCircle2 className="w-5 h-5" style={{ color: '#0058a9' }} />
                </div>
                <div>
                  <p
                    className="text-sm font-semibold"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#081120'
                    }}
                  >
                    Proven Track Record
                  </p>
                  <p
                    className="text-xs"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '11px',
                      color: '#555658'
                    }}
                  >
                    Trusted by 500+ companies worldwide
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Banner */}
        {/* <div 
          className="mt-16 rounded-xl p-6 text-center"
          style={{ 
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            border: '1px solid #eef2f6'
          }}
        >
          <p
            className="text-sm"
            style={{ 
              fontFamily: '"Open Sans", sans-serif',
              fontSize: '14px',
              color: '#555658'
            }}
          >
            🚀 Ready to transform your team? <span className="font-semibold" style={{ color: '#0058a9' }}>Schedule a consultation</span> with our staffing experts today
          </p>
        </div> */}
      </div>
    </section>
  );
}